/**
 * Destination Banner Manager
 * /admin/destination-banners
 *
 * Lists every destination the site exposes and lets an admin swap the hero
 * banner (desktop + mobile) for each one. Under the hood we upsert / clear
 * entries in the `assets` collection with keys `hero_<slug>_desktop` and
 * `hero_<slug>_mobile`, which the frontend `AssetContext` already looks up
 * when rendering /destinations/<slug>.
 */
import { useState, useEffect, useCallback } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Image as ImageIcon,
  Upload,
  RotateCcw,
  ExternalLink,
  Loader2,
  Link as LinkIcon,
  CheckCircle2,
  Info,
  History,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Client-side fallback previews for destinations that have no CMS override yet.
// Kept in sync with destinationData.js — purely visual.
const FALLBACK_PREVIEWS = {
  home: 'https://static.prod-images.emergentagent.com/jobs/e5f78efe-5b96-4b62-ab9f-11b1c0606d71/images/1afa39692404bcf78e735905f91b371210e9dae24adb15b1ccd5f349f2442fa5.png',
  usa: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/8987d0699afe7402f73b16854767f98f32ebfd00a18dfe847a37444db5353bd5.png',
  uk: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/6c8de25df10e898a9d3600b71e27425f9b4c560944b630df0b6c5e460794504a.png',
  canada: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c1aa510683c445460131840d513e4f553c79a26742a00f3a471d4fab7e8793a2.png',
  australia: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/485a7f5ddb2e7edd0dac78f0933d7e23f2ee733e4c56470188d7442e87381ff0.png',
  germany: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4819bad9a28616f54a185f58df90b442c70b8243a1b20006c59ea6650ecd722a.png',
  dubai: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=900&q=75',
};

export const DestinationBannerManager = () => {
  const { token } = useCMSAuth();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { slug, label, flag, desktop_url, mobile_url, desktop_alt, mobile_alt }
  const [savingSlug, setSavingSlug] = useState(null);
  const [resetPending, setResetPending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/assets/destination-banners`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDestinations(data.destinations || []);
    } catch (err) {
      toast.error('Could not load destination banners.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (d) => {
    const overrideStats = d.stats_override?.stats;
    const isHome = d.slug === 'home';
    setEditing({
      slug: d.slug,
      label: d.label,
      flag: d.flag,
      is_homepage: isHome,
      desktop_url: d.desktop?.url || '',
      mobile_url: d.mobile?.url || '',
      desktop_alt: d.desktop?.alt_text || '',
      mobile_alt: d.mobile?.alt_text || '',
      desktop_history: d.desktop?.history || [],
      mobile_history: d.mobile?.history || [],
      // Stats bar (4 rows, only relevant for destinations, not homepage)
      stats: isHome
        ? []
        : (overrideStats && overrideStats.length
            ? [...overrideStats, ...Array(Math.max(0, 4 - overrideStats.length)).fill({ value: '', label: '' })].slice(0, 4)
            : [
                { value: '', label: 'Universities' },
                { value: '', label: "Int'l Students" },
                { value: '', label: 'Work Visa' },
                { value: '', label: 'Intake Seasons' },
              ]),
    });
  };

  const closeEditor = () => setEditing(null);

  const validateUrl = (u) => {
    if (!u) return true; // empty is allowed = clear override
    try {
      const url = new URL(u);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const saveBanner = async () => {
    if (!editing) return;
    if (!validateUrl(editing.desktop_url) || !validateUrl(editing.mobile_url)) {
      toast.error('Please enter valid http(s) URLs.');
      return;
    }
    setSavingSlug(editing.slug);
    try {
      const payload = {
        slug: editing.slug,
        desktop_url: editing.desktop_url,
        mobile_url: editing.mobile_url,
        desktop_alt: editing.desktop_alt,
        mobile_alt: editing.mobile_alt,
      };
      // Include stats only for non-homepage cards
      if (!editing.is_homepage && Array.isArray(editing.stats)) {
        payload.stats = editing.stats
          .map((s) => ({ value: (s.value || '').trim(), label: (s.label || '').trim() }))
          .filter((s) => s.value || s.label);
      }
      const res = await fetch(`${API_URL}/api/assets/destination-banners`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${editing.label} banner updated.`);
      closeEditor();
      load();
    } catch (err) {
      toast.error('Save failed. Please try again.');
    } finally {
      setSavingSlug(null);
    }
  };

  const resetBanner = async (slug) => {
    setSavingSlug(slug);
    try {
      const res = await fetch(`${API_URL}/api/assets/destination-banners/${slug}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Reverted to default banner.');
      setResetPending(null);
      load();
    } catch (err) {
      toast.error('Reset failed. Please try again.');
    } finally {
      setSavingSlug(null);
    }
  };

  // File upload -> POST /api/assets/upload (Cloudinary) -> get back URL -> put into editing state
  const [uploadingSide, setUploadingSide] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);

  // Fetch presets when the editor opens for a destination
  useEffect(() => {
    if (!editing?.slug) { setPresets([]); return; }
    let cancelled = false;
    setLoadingPresets(true);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/assets/banner-presets/${editing.slug}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!cancelled) setPresets(Array.isArray(data.presets) ? data.presets : []);
      } catch {
        if (!cancelled) setPresets([]);
      } finally {
        if (!cancelled) setLoadingPresets(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editing?.slug]);
  const uploadFile = async (side, file) => {
    if (!file || !editing) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is too large (max 8MB).');
      return;
    }
    setUploadingSide(side);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', `${editing.label} — Hero (${side})`);
      formData.append('asset_key', `hero_${editing.slug}_${side}`);
      formData.append('type', 'hero');
      formData.append('category', 'destinations');
      formData.append('description', `Destination banner for ${editing.label} — ${side}`);
      const res = await fetch(`${API_URL}/api/assets/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const url = data.url || data.asset?.url;
      if (!url) throw new Error('No URL returned');
      setEditing((prev) => ({ ...prev, [`${side}_url`]: url }));
      toast.success(`Uploaded ${side} image.`);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploadingSide(null);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="destination-banner-manager">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-ux-cyan" /> Destination Banners
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Replace the hero banner shown on <code>/destinations/&lt;country&gt;</code>. Upload an
            image or paste a URL. Both desktop (wide) and mobile (portrait) versions are supported —
            the same image works for both if a dedicated mobile crop isn&apos;t available.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {destinations.map((d) => {
              const currentUrl = d.desktop?.url || FALLBACK_PREVIEWS[d.slug] || null;
              const isOverridden = !!d.desktop || !!d.mobile;
              return (
                <Card
                  key={d.slug}
                  data-testid={`banner-card-${d.slug}`}
                  className="overflow-hidden"
                >
                  <div
                    className="h-40 bg-slate-200 relative bg-cover bg-center"
                    style={currentUrl ? { backgroundImage: `url(${currentUrl})` } : {}}
                  >
                    {!currentUrl && (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
                        No preview
                      </div>
                    )}
                    {isOverridden && (
                      <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-500/90 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> CMS override
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{d.flag}</span>
                        <span className="font-semibold">{d.label}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Desktop key: <code className="text-[10px]">hero_{d.slug}_desktop</code>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => openEditor(d)}
                        data-testid={`banner-edit-${d.slug}`}
                      >
                        <Upload className="w-4 h-4 mr-1.5" /> Change banner
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                      >
                        <a
                          href={d.slug === 'home' ? '/' : `/destinations/${d.slug === 'new_zealand' ? 'new-zealand' : d.slug}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink className="w-4 h-4 mr-1.5" /> Preview
                        </a>
                      </Button>
                      {isOverridden && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setResetPending(d)}
                          data-testid={`banner-reset-${d.slug}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing?.flag} Change banner — {editing?.label}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 pt-1">
              Upload a fresh image or paste a URL. Leave a field empty and save to remove that
              override.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2">
            {['desktop', 'mobile'].map((side) => {
              const history = editing?.[`${side}_history`] || [];
              return (
                <div key={side} className="space-y-2 pb-4 border-b last:border-b-0">
                  <Label className="font-semibold text-sm capitalize">
                    {side} banner {side === 'desktop' ? '(1920×720 recommended)' : '(900×1200 recommended)'}
                  </Label>
                  {editing?.[`${side}_url`] && (
                    <div className="h-32 rounded-md bg-slate-100 bg-cover bg-center" style={{ backgroundImage: `url(${editing[`${side}_url`]})` }} />
                  )}
                  <div className="flex gap-2">
                    <label
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-md cursor-pointer transition"
                      data-testid={`banner-upload-${side}`}
                    >
                      {uploadingSide === side ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5" />
                      )}
                      Upload file
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => uploadFile(side, e.target.files?.[0])}
                      />
                    </label>
                    <div className="flex-1 flex items-center gap-2">
                      <LinkIcon className="w-3.5 h-3.5 text-slate-400" />
                      <Input
                        placeholder="…or paste an image URL (https://…)"
                        value={editing?.[`${side}_url`] || ''}
                        onChange={(e) =>
                          setEditing((prev) => ({ ...prev, [`${side}_url`]: e.target.value }))
                        }
                        data-testid={`banner-url-${side}`}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <Input
                    placeholder={`Alt text for ${side} (a11y & SEO)`}
                    value={editing?.[`${side}_alt`] || ''}
                    onChange={(e) =>
                      setEditing((prev) => ({ ...prev, [`${side}_alt`]: e.target.value }))
                    }
                    data-testid={`banner-alt-${side}`}
                    className="h-8 text-xs"
                  />
                  {history.length > 0 && (
                    <div className="space-y-1.5" data-testid={`banner-history-${side}`}>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                        <History className="w-3 h-3" /> Recently used ({history.length}) — click to restore
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {history.map((h, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() =>
                              setEditing((prev) => ({ ...prev, [`${side}_url`]: h.url }))
                            }
                            className="group relative inline-block w-20 h-14 rounded-md overflow-hidden border border-slate-200 hover:border-ux-cyan transition"
                            title={h.url}
                            data-testid={`banner-history-item-${side}-${idx}`}
                          >
                            <div
                              className="w-full h-full bg-cover bg-center"
                              style={{ backgroundImage: `url(${h.url})` }}
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                              <RotateCcw className="w-3.5 h-3.5 text-white" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Preset library — curated on-brand photos per destination */}
            {(presets.length > 0 || loadingPresets) && (
              <div className="space-y-3 pt-2" data-testid="banner-presets">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold text-sm">On-brand preset library</Label>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Curated stock photos for {editing?.label} — pick one to apply to both desktop & mobile.
                    </p>
                  </div>
                  {loadingPresets && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {presets.map((p) => {
                    const isSelected = editing?.desktop_url === p.url || editing?.mobile_url === p.url;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setEditing((prev) => ({
                            ...prev,
                            desktop_url: p.url,
                            mobile_url: p.url.replace('w=1920', 'w=900').replace('q=80', 'q=75'),
                          }));
                          toast.success(`Applied "${p.label}"`);
                        }}
                        className={`group relative rounded-md overflow-hidden border-2 transition ${
                          isSelected ? 'border-ux-cyan ring-2 ring-ux-cyan/30' : 'border-slate-200 hover:border-slate-400'
                        }`}
                        title={p.label}
                        data-testid={`banner-preset-${p.id}`}
                      >
                        <div
                          className="w-full h-20 bg-cover bg-center"
                          style={{ backgroundImage: `url(${p.url.replace('w=1920', 'w=400')})` }}
                        />
                        <div className="p-1.5 text-[10px] font-medium text-left truncate bg-white">
                          {p.label}
                        </div>
                        {isSelected && (
                          <div className="absolute top-1 right-1 bg-ux-cyan text-white rounded-full p-0.5">
                            <CheckCircle2 className="w-3 h-3" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stats bar editor (destination pages only, not homepage) */}
            {!editing?.is_homepage && Array.isArray(editing?.stats) && (
              <div className="space-y-3 pt-2" data-testid="banner-stats-editor">
                <Label className="font-semibold text-sm">Stats bar under the banner</Label>
                <p className="text-[11px] text-slate-500 -mt-1">
                  Four gradient cards shown right below the hero. Leave value empty to fall back to defaults.
                </p>
                {editing.stats.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      className="h-8 text-xs"
                      placeholder={`Value #${idx + 1} (e.g. "400+" or "18 Month Job Seeker")`}
                      value={row.value || ''}
                      onChange={(e) => {
                        const next = [...editing.stats];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setEditing((prev) => ({ ...prev, stats: next }));
                      }}
                      data-testid={`banner-stat-value-${idx}`}
                    />
                    <Input
                      className="h-8 text-xs"
                      placeholder={`Label #${idx + 1} (e.g. "Universities")`}
                      value={row.label || ''}
                      onChange={(e) => {
                        const next = [...editing.stats];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setEditing((prev) => ({ ...prev, stats: next }));
                      }}
                      data-testid={`banner-stat-label-${idx}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-100 text-xs text-blue-800 p-3 flex gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Cloudinary auto-serves optimised versions. Uploaded files persist under
              <code className="bg-white/70 px-1 mx-1 rounded">assets/destinations/</code>. External
              URLs (e.g. Unsplash) are supported too — no need to re-host.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              onClick={saveBanner}
              disabled={savingSlug === editing?.slug}
              data-testid="banner-save-button"
            >
              {savingSlug === editing?.slug ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                'Save banner'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirm */}
      <Dialog open={!!resetPending} onOpenChange={(o) => !o && setResetPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revert {resetPending?.label} banner?</DialogTitle>
            <DialogDescription>
              This clears the CMS override. The site will fall back to the default banner shipped
              with the destination page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPending(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => resetPending && resetBanner(resetPending.slug)}
              disabled={savingSlug === resetPending?.slug}
              data-testid="banner-confirm-reset"
            >
              {savingSlug === resetPending?.slug ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reverting…
                </>
              ) : (
                'Yes, revert'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default DestinationBannerManager;
